import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Bell, BookOpen, Camera, Compass, Heart, Music, Sparkles, ChevronRight } from "lucide-react";

export default function HomeScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: n }] = await Promise.all([
          api.get("/scans"),
          api.get("/notifications"),
        ]);
        setScans((s.scans || []).slice(0, 8));
        setUnread((n.notifications || []).filter((x) => !x.read).length);
      } catch {}
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "Friend";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <Shell section="home">
      {/* Top bar */}
      <div className="flex items-start justify-between mb-6 fade-up">
        <div>
          <p className="text-muted-soft text-xs uppercase tracking-wider">{greeting},</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-shadow-green mt-1" data-testid="home-greeting">
            {firstName} 🌱
          </h1>
        </div>
        <button
          onClick={() => navigate("/notifications")}
          data-testid="home-bell"
          className="relative w-11 h-11 rounded-full glass-card flex items-center justify-center"
        >
          <Bell size={18} className="text-white" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center"
                  style={{ boxShadow: "var(--glow-red)" }}>
              {unread}
            </span>
          )}
        </button>
      </div>

      {/* Quick actions — spec: 160px height, gradient left-edge glow, teal for diagnose */}
      <div className="grid grid-cols-2 gap-4 mb-7">
        <button
          onClick={() => navigate("/identify")}
          data-testid="home-action-identify"
          className="glass-card p-5 text-left flex flex-col justify-between hover:scale-[1.02] transition-transform fade-up stagger-1"
          style={{
            height: 160,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: 0, top: 0, bottom: 0, width: 4,
              background: "linear-gradient(180deg, #4CAF50, rgba(76,175,80,0.4))",
              boxShadow: "0 0 16px rgba(76,175,80,0.8)",
            }}
          />
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl"
            style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
          >
            <Camera size={28} className="text-[#4CAF50]" strokeWidth={1.5} />
          </span>
          <div>
            <h3 className="font-semibold text-base">Identify Plant</h3>
            <p className="text-soft text-xs mt-1">Snap or upload a photo</p>
          </div>
        </button>
        <button
          onClick={() => navigate("/diagnose")}
          data-testid="home-action-diagnose"
          className="glass-card p-5 text-left flex flex-col justify-between hover:scale-[1.02] transition-transform fade-up stagger-2"
          style={{
            height: 160,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: 0, top: 0, bottom: 0, width: 4,
              background: "linear-gradient(180deg, #26A69A, rgba(38,166,154,0.4))",
              boxShadow: "0 0 16px rgba(38,166,154,0.8)",
            }}
          />
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl"
            style={{ background: "rgba(38,166,154,0.18)", boxShadow: "0 0 18px rgba(38,166,154,0.5)" }}
          >
            <Camera size={28} className="text-[#26A69A]" strokeWidth={1.5} />
          </span>
          <div>
            <h3 className="font-semibold text-base">Diagnose Disease</h3>
            <p className="text-soft text-xs mt-1">Spot pests & illnesses</p>
          </div>
        </button>
      </div>

      {/* Secondary actions: Healing library + Explore + Journal + Music */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        <button
          onClick={() => navigate("/healing")}
          data-testid="home-action-healing"
          className="glass-card p-3 text-left flex flex-col gap-2 hover:scale-[1.02] transition-transform fade-up stagger-3"
          style={{ background: "rgba(0,0,0,0.42)", border: "1px solid rgba(76,175,80,0.22)" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
            style={{ background: "rgba(76,175,80,0.18)", boxShadow: "0 0 12px rgba(76,175,80,0.35)" }}
          >
            <Heart size={16} className="text-[#4CAF50]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold text-[12px] leading-tight">Healing</h3>
            <p className="text-soft text-[10px] mt-0.5 truncate">Remedies</p>
          </div>
        </button>
        <button
          onClick={() => navigate("/search")}
          data-testid="home-action-explore"
          className="glass-card p-3 text-left flex flex-col gap-2 hover:scale-[1.02] transition-transform fade-up stagger-4"
          style={{ background: "rgba(0,0,0,0.42)", border: "1px solid rgba(76,175,80,0.22)" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
            style={{ background: "rgba(255,213,79,0.16)", boxShadow: "0 0 12px rgba(255,213,79,0.3)" }}
          >
            <Compass size={16} className="text-[#FFD54F]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold text-[12px] leading-tight">Explore</h3>
            <p className="text-soft text-[10px] mt-0.5 truncate">Browse plants</p>
          </div>
        </button>
        <button
          onClick={() => navigate("/journal")}
          data-testid="home-action-journal"
          className="glass-card p-3 text-left flex flex-col gap-2 hover:scale-[1.02] transition-transform fade-up stagger-5"
          style={{ background: "rgba(0,0,0,0.42)", border: "1px solid rgba(123,31,162,0.30)" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 relative"
            style={{ background: "rgba(123,31,162,0.20)", boxShadow: "0 0 12px rgba(123,31,162,0.4)" }}
          >
            <BookOpen size={16} className="text-[#BA68C8]" strokeWidth={1.6} />
            {user?.tier !== "platinum" && (
              <span
                className="absolute -top-1 -right-1 text-[8px] font-bold uppercase px-1 rounded-full"
                style={{ background: "#FFD54F", color: "#000", letterSpacing: "0.04em" }}
              >
                P
              </span>
            )}
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold text-[12px] leading-tight">Journal</h3>
            <p className="text-soft text-[10px] mt-0.5 truncate">Track growth</p>
          </div>
        </button>
        <button
          onClick={() => navigate("/music")}
          data-testid="home-action-music"
          className="glass-card p-3 text-left flex flex-col gap-2 hover:scale-[1.02] transition-transform fade-up stagger-6"
          style={{ background: "rgba(0,0,0,0.42)", border: "1px solid rgba(38,166,154,0.30)" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
            style={{ background: "rgba(38,166,154,0.20)", boxShadow: "0 0 12px rgba(38,166,154,0.4)" }}
          >
            <Music size={16} className="text-[#4DB6AC]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold text-[12px] leading-tight">Music</h3>
            <p className="text-soft text-[10px] mt-0.5 truncate">Plant frequencies</p>
          </div>
        </button>
      </div>

      {/* Upgrade banner */}
      {user?.tier === "free" && (
        <div
          className="glass-card p-5 mb-7 fade-up stagger-3 flex items-center gap-4"
          style={{ borderColor: "rgba(255,143,0,0.4)", boxShadow: "var(--glow-amber)" }}
          data-testid="home-upgrade-banner"
        >
          <Sparkles size={28} className="text-[#FF8F00] flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm">Unlock 5 disease remedies + climate care</h3>
            <p className="text-soft text-xs mt-0.5">Upgrade to Premium and care like a pro.</p>
          </div>
          <button onClick={() => navigate("/subscription")} className="btn-amber text-xs px-4 py-2">
            Upgrade
          </button>
        </div>
      )}

      {/* Recent scans */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold uppercase tracking-wider text-soft">Recent Scans</h2>
        <button
          onClick={() => navigate("/collection")}
          className="text-[#4CAF50] text-sm flex items-center gap-1 hover:gap-2 transition-all"
          data-testid="home-see-all"
        >
          See all <ChevronRight size={14} />
        </button>
      </div>

      {scans.length === 0 ? (
        <div className="glass-card p-8 text-center fade-up">
          <p className="text-soft text-sm">No scans yet.</p>
          <button onClick={() => navigate("/identify")} className="btn-primary mt-4">
            Identify your first plant
          </button>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {scans.map((s, idx) => (
            <button
              key={s.scan_id}
              onClick={() => navigate(`/${s.type === "identify" ? "identify" : "diagnose"}/result/${s.scan_id}`)}
              className={`glass-card p-3 w-32 flex-shrink-0 text-left fade-up stagger-${(idx % 5) + 1}`}
              data-testid={`recent-scan-${idx}`}
            >
              <div
                className="h-20 rounded-lg mb-2"
                style={{
                  background: s.type === "identify" ? "rgba(76,175,80,0.15)" : "rgba(255,143,0,0.15)",
                  border: `1px solid ${s.type === "identify" ? "rgba(76,175,80,0.3)" : "rgba(255,143,0,0.3)"}`,
                }}
              />
              <p className="text-xs font-semibold truncate">
                {s.result?.species?.[0]?.common_name || s.result?.disease?.name || "Scan"}
              </p>
              <p className="text-[10px] text-muted-soft mt-0.5">
                {s.type === "identify" ? "ID" : "Disease"}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Ad placeholder (free tier) */}
      {user?.tier === "free" && (
        <div
          className="mt-6 mb-2 glass-card p-3 text-center text-[11px] text-white/40"
          data-testid="home-ad-banner"
        >
          Advertisement — Upgrade to remove ads
        </div>
      )}
    </Shell>
  );
}
